import React from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { ArrowLeft, Clock, User, Calendar, Share2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";

const BuildingTrustAgenticAI = () => {
  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1">
        {/* Hero Section */}
        <section className="py-8 md:py-12 bg-white">
          <div className="container mx-auto px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <Button variant="ghost" asChild className="mb-8 text-[#8932c8] hover:text-[#7a2cb3] hover:bg-[#8932c8]/5">
                <Link to="/blogs" className="flex items-center gap-2">
                  <ArrowLeft className="h-4 w-4" />
                  Back to Blog
                </Link>
              </Button>
              
              <div className="rounded-xl overflow-hidden shadow-sm mb-6">
                <img 
                  src="/images/ai-robot-hand-human.jpg" 
                  alt="Building Trust in Agentic AI" 
                  className="w-full h-auto"
                />
              </div>
              
              <p className="text-sm text-gray-500 mb-8 italic text-center">
                Building confidence in autonomous AI systems for critical security operations
              </p>
              
              <div className="flex flex-wrap gap-2 mb-6">
                <span className="text-xs font-medium bg-[#8932c8]/10 text-[#8932c8] px-3 py-1 rounded-full">
                  Agentic AI
                </span>
                <span className="text-xs font-medium bg-[#8932c8]/10 text-[#8932c8] px-3 py-1 rounded-full">
                  SOC Operations
                </span>
                <span className="text-xs font-medium bg-[#8932c8]/10 text-[#8932c8] px-3 py-1 rounded-full">
                  Trust & Security
                </span>
              </div>
              
              <h1 className="text-3xl md:text-4xl font-bold mb-6 leading-tight text-gray-900">
                Building Trust in Agentic AI: How MSSPs and Security Teams Can Confidently Rely on Autonomous AI in SOC Operations
              </h1>
              
              <div className="flex items-center text-sm text-gray-500 mb-12 pb-6 border-b border-gray-200">
                <User className="h-4 w-4 mr-1" />
                <span className="mr-4">Spharaka Security Team</span>
                <Calendar className="h-4 w-4 mr-1" />
                <span className="mr-4">August 15, 2025</span>
                <Clock className="h-4 w-4 mr-1" />
                <span>12 min read</span>
              </div>
            </div>
          </div>
        </section>

        {/* Article Content */}
        <section className="bg-white pb-16">
          <div className="container mx-auto px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <div className="prose prose-lg prose-gray max-w-none" style={{ fontSize: '18px', lineHeight: '1.7' }}>
                <p className="text-gray-700 mb-8 leading-relaxed">
                  Agentic AI systems - autonomous AI agents that detect, investigate, and respond to cybersecurity threats - are rapidly transforming modern Security Operations Centers (SOCs). For MSSPs and cybersecurity teams, these AI agents offer unprecedented scale and efficiency, operating 24/7 without fatigue and slashing response times.
                </p>

                <p className="mb-8 text-gray-700">
                  Yet, the adoption hurdle remains: How can security professionals trust AI to execute important SOC tasks autonomously without sacrificing accuracy, explainability, and control?
                </p>

                <p className="mb-12 text-gray-700">
                  This blog guides MSSPs, incident responders, and security analysts through the essential considerations for trusting and collaborating with Agentic AI in SOCs.
                </p>

                <h2 className="text-3xl font-bold mb-6 text-gray-900">Understanding the Trust Challenge with Agentic AI</h2>
                
                <p className="mb-6 text-gray-700">
                  Traditional security tools either automate fixed playbooks or provide alerts requiring human review. Agentic AI differs fundamentally; it perceives, reasons, plans, and acts autonomously across the alert lifecycle - often without human prompts.
                </p>

                <p className="mb-4 text-gray-700">This capability means:</p>
                
                <ul className="mb-8 space-y-2 text-gray-700">
                  <li>Agents can triage alerts, conduct investigations, enrich context, recommend or even execute remediation automatically.</li>
                  <li>They dynamically create and adapt plans based on scenario context instead of following rigid rules.</li>
                </ul>

                <p className="mb-12 text-gray-700">
                  While this autonomy boosts SOC efficiency, it also raises valid concerns around transparency, explainability, decision rationale, and control boundaries. Trust hinges on establishing assurances that AI actions are accurate, auditable, and within defined guardrails.
                </p>

                <h2 className="text-3xl font-bold mb-6 text-gray-900">Key Trust-Building Strategies for SOC Teams</h2>

                <h3 className="text-2xl font-bold mt-10 mb-4 text-gray-800">Start with Transparency and Explainability</h3>
                
                <p className="mb-4 text-gray-700">Choose Agentic AI platforms that provide:</p>
                
                <ul className="mb-8 space-y-2 text-gray-700">
                  <li><strong>Clear Decision Trails:</strong> View exactly how agents arrive at conclusions through step-by-step reasoning logs.</li>
                  <li><strong>Evidence Attribution:</strong> See which data sources, rules, and analysis contributed to each decision.</li>
                  <li><strong>Confidence Scores:</strong> Understand agent certainty levels to help analysts prioritize reviews.</li>
                  <li><strong>Natural Language Explanations:</strong> Get human-readable summaries of agent actions and findings.</li>
                </ul>

                <h3 className="text-2xl font-bold mt-10 mb-4 text-gray-800">Implement Human-in-the-Loop Controls</h3>
                
                <p className="mb-4 text-gray-700">Build trust through graduated autonomy:</p>
                
                <ul className="mb-8 space-y-2 text-gray-700">
                  <li><strong>Approval Gates:</strong> Require human confirmation for high-impact actions like quarantining systems or blocking users.</li>
                  <li><strong>Escalation Thresholds:</strong> Automatically involve analysts when confidence drops below predefined levels.</li>
                  <li><strong>Continuous Feedback Loops:</strong> Allow analysts to correct, validate, or modify agent decisions to improve future performance.</li>
                  <li><strong>Role-Based Permissions:</strong> Ensure agents only execute actions within defined organizational policies.</li>
                </ul>

                <h3 className="text-2xl font-bold mt-10 mb-4 text-gray-800">Validate Performance Through Metrics and Testing</h3>
                
                <p className="mb-4 text-gray-700">Build confidence with measurable outcomes:</p>
                
                <ul className="mb-8 space-y-2 text-gray-700">
                  <li><strong>Accuracy Tracking:</strong> Monitor false positive/negative rates and compare against human baseline performance.</li>
                  <li><strong>Response Time Improvements:</strong> Measure mean time to detect (MTTD) and mean time to respond (MTTR) improvements.</li>
                  <li><strong>Coverage Analysis:</strong> Verify that agents handle the full spectrum of threat scenarios in your environment.</li>
                  <li><strong>Red Team Testing:</strong> Regularly test agent effectiveness against simulated attacks and novel threat vectors.</li>
                </ul>

                <h3 className="text-2xl font-bold mt-10 mb-4 text-gray-800">Establish Governance and Compliance Frameworks</h3>
                
                <p className="mb-4 text-gray-700">Ensure responsible AI deployment:</p>
                
                <ul className="mb-8 space-y-2 text-gray-700">
                  <li><strong>Clear Boundaries:</strong> Define precisely which tasks agents can execute without human oversight.</li>
                  <li><strong>Audit Trails:</strong> Maintain comprehensive logs of all agent actions for compliance and forensic analysis.</li>
                  <li><strong>Regular Reviews:</strong> Schedule periodic assessments of agent performance and update policies as needed.</li>
                  <li><strong>Incident Handling:</strong> Establish procedures for when agents make mistakes or encounter edge cases.</li>
                </ul>

                <h2 className="text-3xl font-bold mb-6 text-gray-900">Practical Steps for MSSP Implementation</h2>

                <h3 className="text-2xl font-bold mt-10 mb-4 text-gray-800">Phase 1: Pilot with Low-Risk Tasks</h3>
                
                <ul className="mb-8 space-y-2 text-gray-700">
                  <li>Start with basic alert enrichment and initial triage activities.</li>
                  <li>Focus on high-volume, routine tasks where mistakes have minimal impact.</li>
                  <li>Build team familiarity with agent capabilities and limitations.</li>
                </ul>

                <h3 className="text-2xl font-bold mt-10 mb-4 text-gray-800">Phase 2: Expand with Supervised Autonomy</h3>
                
                <ul className="mb-8 space-y-2 text-gray-700">
                  <li>Introduce more complex investigation and correlation tasks.</li>
                  <li>Maintain human oversight for all remediation actions.</li>
                  <li>Use agent recommendations to augment analyst decision-making.</li>
                </ul>

                <h3 className="text-2xl font-bold mt-10 mb-4 text-gray-800">Phase 3: Full Autonomous Operation</h3>
                
                <ul className="mb-8 space-y-2 text-gray-700">
                  <li>Enable agents to execute predetermined response actions automatically.</li>
                  <li>Focus human attention on high-priority, complex threats.</li>
                  <li>Continuously monitor and tune agent performance.</li>
                </ul>

                <h2 className="text-3xl font-bold mb-6 text-gray-900">Building Long-term Trust</h2>

                <p className="mb-6 text-gray-700">
                  Trust in Agentic AI is not a destination but an ongoing journey. Success requires commitment to transparency, continuous learning, and adaptive governance.
                </p>

                <p className="mb-6 text-gray-700">
                  Organizations that invest in proper trust-building frameworks will unlock the full potential of autonomous AI while maintaining the control and confidence essential for mission-critical security operations.
                </p>

                <h2 className="text-3xl font-bold mb-6 text-gray-900">Conclusion</h2>

                <p className="mb-6 text-gray-700">
                  Agentic AI represents the future of SOC operations, offering unprecedented efficiency and coverage. However, successful adoption requires more than just deploying the technology - it demands building genuine trust through transparency, control, and demonstrated performance.
                </p>
                
                <p className="mb-8 text-gray-700">
                  MSSPs and security teams that approach Agentic AI with the right trust-building strategies will not only improve their operational capabilities but also position themselves as leaders in the next generation of cybersecurity services.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Share Section */}
        <section className="py-12 bg-white border-t">
          <div className="container mx-auto px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
                <div className="flex items-center gap-4">
                  <span className="text-sm font-medium text-gray-600">Share this article:</span>
                  <Button variant="outline" size="sm" className="flex items-center gap-2 border-[#8932c8] text-[#8932c8] hover:bg-[#8932c8]/5">
                    <Share2 className="h-4 w-4" />
                    Share
                  </Button>
                </div>
                <Button asChild className="bg-[#8932c8] hover:bg-[#7a2cb3]">
                  <Link to="/contact">Get Started with Spharaka XDR</Link>
                </Button>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default BuildingTrustAgenticAI;